import { useState } from 'react';
import { CheckCircle2, Circle, FileText, PenLine, RefreshCw, ShieldAlert } from 'lucide-react';
import { cn } from '@/lib/cn';
import { formatWhen } from '@/lib/format';
import {
  ECMR_ROLE_LABEL,
  ECMR_STATUS_LABEL,
  openEcmrPdf,
  useEcmr,
  useEcmrRefresh,
  useEcmrSign,
  type EcmrRole,
} from '@/api/hooks/ecmr';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { Dialog } from '@/components/ui/Dialog';
import { Field, Input, Select, Textarea } from '@/components/ui/Field';
import { ErrorState, LoadingBlock } from '@/components/ui/State';
import { toast } from '@/stores/toast';

const ROLES: EcmrRole[] = ['sender', 'carrier', 'consignee'];

/**
 * Електронната ЧМР на заявката: трите подписа като три отметки, PDF с един
 * бутон. Подписите идват от телефона на шофьора; офисът може да отбележи
 * подпис, положен на хартия.
 */
export function EcmrCard({ orderId }: { orderId: string }) {
  const { data, isLoading, isError, refetch } = useEcmr(orderId);
  const refresh = useEcmrRefresh(orderId);
  const [signing, setSigning] = useState(false);
  const [opening, setOpening] = useState(false);

  return (
    <Card>
      <CardHeader
        title={
          <span className="inline-flex items-center gap-2">
            <FileText className="size-4 text-accent" /> Електронна ЧМР
          </span>
        }
        subtitle={data ? `№ ${data.number}` : undefined}
        actions={
          data ? (
            <Badge tone={data.status === 'delivered' ? 'ok' : data.status === 'issued' ? 'accent' : data.status === 'cancelled' ? 'danger' : 'warn'}>
              {ECMR_STATUS_LABEL[data.status]}
            </Badge>
          ) : null
        }
      />
      {isLoading ? (
        <LoadingBlock />
      ) : isError || !data ? (
        <ErrorState onRetry={() => void refetch()} />
      ) : (
        <div className="space-y-3 p-4">
          <ul className="grid gap-2 sm:grid-cols-3">
            {ROLES.map((role) => {
              const s = data.signatures.find((x) => x.role === role);
              return (
                <li
                  key={role}
                  className={cn('rounded-lg border p-3', s ? 'border-ok/40 bg-ok-soft' : 'border-line bg-raised')}
                >
                  <div className="flex items-center gap-1.5 text-xs font-medium text-ink-dim">
                    {s ? <CheckCircle2 className="size-4 text-ok" /> : <Circle className="size-4 text-ink-faint" />}
                    {ECMR_ROLE_LABEL[role]}
                  </div>
                  {s ? (
                    <>
                      <div className="mt-1 truncate text-sm font-medium">{s.name}</div>
                      <div className="text-[11px] text-ink-faint">
                        {formatWhen(s.at)} · {s.source === 'office' ? 'от офиса' : 'на телефона'}
                      </div>
                      {s.remarks ? <div className="mt-1 text-xs text-warn">Забележки: {s.remarks}</div> : null}
                    </>
                  ) : (
                    <div className="mt-1 text-sm text-ink-faint">чака подпис</div>
                  )}
                </li>
              );
            })}
          </ul>

          <dl className="grid grid-cols-1 gap-x-6 gap-y-1.5 text-sm sm:grid-cols-2">
            <Row label="Изпращач" value={data.sender.name} />
            <Row label="Получател" value={data.consignee.name} />
            <Row label="Товарене" value={[data.loading.place, data.loading.country].filter(Boolean).join(', ')} />
            <Row label="Доставка" value={[data.delivery.place, data.delivery.country].filter(Boolean).join(', ')} />
            <Row label="Товар" value={data.goods.description} />
            <Row label="Бус · шофьор" value={[data.carrier.vehiclePlate, data.carrier.driverName].filter(Boolean).join(' · ')} />
          </dl>

          {!data.hashIntact ? (
            <div className="flex items-center gap-2 rounded-lg border border-danger/40 bg-danger-soft p-2 text-sm text-danger">
              <ShieldAlert className="size-4" /> Съдържанието е променено след подпис.
            </div>
          ) : null}
          {data.missing.length > 0 ? (
            <p className="text-xs text-ink-dim">Липсва: {data.missing.join(', ')}.</p>
          ) : null}

          <div className="flex flex-wrap gap-2">
            <Button
              variant="primary"
              icon={<FileText className="size-4" />}
              loading={opening}
              onClick={() => {
                setOpening(true);
                openEcmrPdf(orderId)
                  .catch((e: unknown) => toast.error(e instanceof Error ? e.message : 'PDF не се отвори.'))
                  .finally(() => setOpening(false));
              }}
            >
              PDF
            </Button>
            {data.status !== 'delivered' && data.status !== 'cancelled' ? (
              <Button icon={<PenLine className="size-4" />} onClick={() => setSigning(true)}>
                Подпис на хартия
              </Button>
            ) : null}
            {data.signatures.length === 0 ? (
              <Button
                variant="ghost"
                icon={<RefreshCw className="size-4" />}
                loading={refresh.isPending}
                onClick={() => refresh.mutate(undefined, { onSuccess: () => toast.ok('Данните са опреснени от заявката.') })}
              >
                Опресни от заявката
              </Button>
            ) : null}
          </div>
        </div>
      )}
      {signing && data ? <SignDialog orderId={orderId} taken={data.signatures.map((s) => s.role)} onClose={() => setSigning(false)} /> : null}
    </Card>
  );
}

function Row({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="flex gap-2">
      <dt className="w-28 shrink-0 text-ink-faint">{label}</dt>
      <dd className="min-w-0 truncate">{value || '—'}</dd>
    </div>
  );
}

function SignDialog({ orderId, taken, onClose }: { orderId: string; taken: EcmrRole[]; onClose: () => void }) {
  const sign = useEcmrSign(orderId);
  const open = ROLES.filter((r) => !taken.includes(r));
  const [role, setRole] = useState<EcmrRole>(open[0] ?? 'consignee');
  const [name, setName] = useState('');
  const [remarks, setRemarks] = useState('');
  return (
    <Dialog open onClose={onClose} title="Подпис, положен на хартия" description="Когато подписът е сложен на хартиена ЧМР, а не на телефона.">
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          sign.mutate(
            { role, name: name.trim(), ...(remarks.trim() ? { remarks: remarks.trim() } : {}) },
            {
              onSuccess: () => {
                toast.ok('Подписът е отбелязан.');
                onClose();
              },
            },
          );
        }}
      >
        <Field label="Кой подписа">
          <Select value={role} onChange={(e) => setRole(e.target.value as EcmrRole)} options={ROLES.map((r) => ({ value: r, label: ECMR_ROLE_LABEL[r] }))} />
        </Field>
        <Field label="Име и фамилия" required>
          <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </Field>
        {role === 'consignee' ? (
          <Field label="Забележки на получателя (ако има)">
            <Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} />
          </Field>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Отказ
          </Button>
          <Button type="submit" variant="primary" disabled={name.trim().length < 3} loading={sign.isPending}>
            Отбележи
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
